import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Lock, Globe, Check } from 'lucide-react';
import ProgressBar from '../profile/ProgressBar';
import { GUARDRAIL_ORDER, GUARDRAIL_INFO, guardrailStatus, formatPriceCeiling, translateGuardrailLabel, translateGuardrailDescription } from '../../utils/groupGuardrailTiers';

// Live "where do I stand" panel for the proportional monetization/
// visibility guardrail system (backend_stocker/app/utils/monetization/
// monetization_guardrails.py). Built 2026-07-19 specifically so a user
// isn't left guessing how the system works -- shows the current state,
// real progress toward the next one, and what every state actually
// unlocks, computed from the user's own real credibility data (never a
// hypothetical).
const GuardrailTierPanel = ({ statusTier, globalScore }) => {
  const { t } = useTranslation();
  const g = guardrailStatus(statusTier || 'Beginner', globalScore || 0);
  const currentLabel = translateGuardrailLabel(g.state, t);
  const nextLabel = g.nextState ? translateGuardrailLabel(g.nextState, t) : null;

  return (
    <div className="bg-cream-100 dark:bg-white/5 border border-cream-300 dark:border-white/10 rounded-2xl p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        <h3 className="text-base font-semibold text-cream-900 dark:text-white">{t('credibility.guardrailPanel.title')}</h3>
      </div>
      <p className="text-sm text-cream-700 dark:text-gray-400 mb-4">
        {t('credibility.guardrailPanel.subtitle')}
      </p>

      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-cream-900 dark:text-gray-300">
          {t('credibility.guardrailPanel.currentlyPrefix')} <span className="text-cream-900 dark:text-white font-semibold">{currentLabel}</span>{' '}
          <span className="text-cream-700 dark:text-gray-500">({statusTier || 'Beginner'})</span>
        </span>
        {g.nextState && (
          <span className="text-xs text-cream-700 dark:text-gray-500">
            {g.pointsToNextState > 0
              ? t('credibility.guardrailPanel.pointsToNextState', { points: g.pointsToNextState, nextLabel })
              : t('credibility.guardrailPanel.eligibleFor', { nextLabel })}
          </span>
        )}
      </div>

      {g.nextState && (
        <ProgressBar
          value={globalScore || 0}
          max={g.nextStateMinScore}
          size="sm"
          variant={g.pointsToNextState > 0 ? 'default' : 'success'}
          showPercentage={false}
          showValue
        />
      )}

      <div className="mt-5 space-y-2">
        {GUARDRAIL_ORDER.map((state) => {
          const info = GUARDRAIL_INFO[state];
          const isCurrent = state === g.state;
          return (
            <div
              key={state}
              className={`flex items-center gap-3 p-3 rounded-xl border text-sm ${
                isCurrent
                  ? 'bg-blue-100 dark:bg-blue-500/10 border-blue-500/30'
                  : 'bg-cream-200 dark:bg-black/20 border-cream-300 dark:border-white/5'
              }`}
            >
              <div className="w-5 flex-shrink-0">
                {isCurrent ? (
                  <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                ) : (
                  <span className="block w-1.5 h-1.5 rounded-full bg-cream-700 dark:bg-gray-600 mx-auto" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-medium ${isCurrent ? 'text-cream-900 dark:text-white' : 'text-cream-900 dark:text-gray-300'}`}>{translateGuardrailLabel(state, t)}</span>
                  <span className="text-xs text-cream-700 dark:text-gray-500">{t('credibility.guardrailPanel.scoreSuffix', { minScore: info.minScore })}</span>
                  {info.allowsPublicGroup ? (
                    <span className="flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400"><Globe size={11} /> {t('credibility.guardrailPanel.publicGroups')}</span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-cream-700 dark:text-gray-500"><Lock size={11} /> {t('credibility.guardrailPanel.privateOnly')}</span>
                  )}
                </div>
                <p className="text-xs text-cream-700 dark:text-gray-500 mt-0.5">{translateGuardrailDescription(state, t)}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-xs text-cream-700 dark:text-gray-400">{t('credibility.guardrailPanel.priceCap')}</div>
                <div className={`text-sm font-semibold ${isCurrent ? 'text-cream-900 dark:text-white' : 'text-cream-900 dark:text-gray-400'}`}>
                  {formatPriceCeiling(info.priceCeilingCents, t)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GuardrailTierPanel;
